import { useSelector } from 'react-redux'
import { AssigneeOption, selectAllSalesTeamUsers } from '@user/store/salesTeam'
import { useEffect, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { getUserListForSalesTeamsByStructure } from '@user/store/users/hooks/useGetUserListForSalesTeamsByStructure'

export type SalesTeamOption = {
  id: number
  label: string
  branchName: string
  name: string
}

export function useBacksideData() {
  const salesTeamUsers = useSelector(selectAllSalesTeamUsers)
  const [owners, setOwners] = useState<AssigneeOption[]>()
  const {
    dirty,
    isSubmitting,
    isValid,
    values,
    errors,
    touched,
    values: { salesTeamIds, assigneeIds },
    setFieldValue,
  } = useFormikContext<{
    salesTeamIds: number[]
    assigneeIds: string[]
    numDays?: number
  }>()

  const improvedSalesTeamUsers = useMemo(
    () => [
      { id: 'All', userId: 'All', name: 'All Team Members' },
      ...salesTeamUsers,
    ],
    [salesTeamUsers],
  )

  const sortedOwners = useMemo(() => {
    if (!owners || !salesTeamIds || salesTeamIds.length == 0) {
      return []
    }

    const unsortedOwners = [...owners].filter((user) => user.isActive)
    unsortedOwners.sort((a, b) => a.name.localeCompare(b.name))
    unsortedOwners.unshift({
      id: 'null',
      name: 'Unassigned',
      userId: 'null',
      isActive: true,
    })

    return unsortedOwners
  }, [owners, salesTeamIds, values])

  const assigneeIdsValue = useMemo(() => {
    if (!assigneeIds) return []

    return [...sortedOwners].filter((owner: AssigneeOption) =>
      assigneeIds.includes(owner.id),
    )
  }, [sortedOwners, assigneeIds])

  useEffect(() => {
    // Fetch data or perform any side effect
    const fetchData = async () => {
      setOwners(await getUserListForSalesTeamsByStructure(salesTeamIds))
    }

    fetchData()
  }, [salesTeamIds])

  useEffect(() => {
    const filteredAssignees = assigneeIds.filter((assignee) => {
      return owners?.some(
        (owner) => owner.id === assignee || assignee === 'null',
      )
    })

    setFieldValue('assigneeIds', filteredAssignees)
  }, [owners, setFieldValue])

  return {
    improvedSalesTeamUsers,
    dirty,
    isSubmitting,
    isValid,
    errors,
    touched,
    assigneeIdsValue,
    owners: sortedOwners,
  }
}
